// src/components/questionnaire/DynamicQuestionnaireForm.jsx
/* ------------------------------------------------------------------ */
/* DynamicQuestionnaireForm – runtime-driven multi-step assessment     */
/* ------------------------------------------------------------------ */
/**
 * Flow
 * ----
 * 1. **Load definition** – pulls an array of questions from
 *    `utils/questionnaire/questions/{name}.js` via `loadQuestionnaire()`.
 * 2. **Filter** – drops questions whose `condition(formData[, user])` returns
 *    `false` (`filterQuestions()`).
 * 3. **Build steps** – `buildSteps()` converts the flat list into renderable
 *    *steps*:
 *        • `single`  – one question<br/>
 *        • `group`   – consecutive questions sharing the same `group` key<br/>
 *        • `review`  – read-only summary before submission (auto-appended)
 * 4. **Prefill** – `prefillRangeDefaults()` seeds untouched `range` controls
 *    with their `min` value so validation doesn’t block the user.
 * 5. **Submit** – on the final step the user’s answers are uploaded to Firebase
 *    Storage as JSON at:<br/>
 *    `questionnaires/{uid}/{name}/{ISO-timestamp}-{5rnd}.json`
 *
 * @component
 * @returns {JSX.Element}
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStorage } from "firebase/storage";
import LinearProgress from "@mui/material/LinearProgress";

import { useAuth } from "../../context/AuthContext";
import {
  loadQuestionnaire,
  filterQuestions,
  buildSteps,
  prefillRangeDefaults,
} from "../../utils/questionnaireQuestions/questionnaireRuntime";

import { isQuestionValid } from "../../utils/form/isQuestionValid";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { renderStep } from "../../utils/questionnaireQuestions/renderStep";
import { uploadResponses } from "../../utils/questionnaireQuestions/uploadResponses";
import { useTranslation } from "react-i18next";
import FormNavButtons from "../form/FormNavButtons";

/* ------------------------------------------------------------------ */
/* React component                                                    */
/* ------------------------------------------------------------------ */
function DynamicQuestionnaireForm() {
  const { user } = useAuth();
  const { questionnaireName } = useParams();

  const translationForm = questionnaireName;
  const { t } = useTranslation(questionnaireName); // form-specific translations
  const { t: tCommon } = useTranslation(); // default/common translations

  const navigate = useNavigate();

  /* ---------------- State ------------------------------------------ */
  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);

  /* ---------------- Load questionnaire ----------------------------- */
  useEffect(() => {
    loadQuestionnaire(questionnaireName)
      .then(setQuestions)
      .catch((err) => {
        console.error("Failed to load questionnaire:", err);
        setQuestions([]);
      });
  }, [questionnaireName]);

  /* ---------------- Derived values -------------------------------- */
  const filteredQuestions = useMemo(
    () => filterQuestions(questions, formData, user),
    [questions, formData, user],
  );

  const steps = useMemo(
    () => buildSteps(filteredQuestions),
    [filteredQuestions],
  );
  const totalSteps = steps.length;
  const progress = (currentStep / totalSteps) * 100;

  /* ---------------- Prefill range defaults ------------------------ */
  useEffect(() => {
    const defaults = prefillRangeDefaults(filteredQuestions, formData);
    if (Object.keys(defaults).length) {
      setFormData((prev) => ({ ...prev, ...defaults }));
    }
  }, [filteredQuestions, formData]);

  /* ---------------- Handlers -------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const goPrev = (e) => {
    e.preventDefault();
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  const goNext = async (e) => {
    e?.preventDefault();

    /* ---------- Final step → upload & redirect -------------------- */
    if (currentStep === totalSteps - 1) {
      try {
        await uploadResponses(
          questionnaireName,
          formData,
          user.uid,
          getStorage(),
        );
        // navigate("/questionnaires");
        navigate("/home");
      } catch (err) {
        console.error("Upload failed:", err);
        alert(tCommon("form.failedSaveAlert"));
      }
      return;
    }

    /* ---------- Validate current step ----------------------------- */
    const step = steps[currentStep];
    const invalid =
      step.type === "single"
        ? !isQuestionValid(step.question, formData)
        : step.questions.some((q) => !isQuestionValid(q, formData));

    if (invalid) {
      alert(tCommon("form.reqFieldsAlert"));
      return;
    }

    setCurrentStep((s) => s + 1);
  };

  /* ---------------- Render ---------------------------------------- */
  return (
    <div className="user-form w-full h-fit flex flex-col gap-4">
      <LinearProgress
        variant="determinate"
        value={progress}
        className="rounded-full"
        sx={{
          backgroundColor: "#B776B9",
          "& .MuiLinearProgress-bar": { backgroundColor: getPrimaryColour() },
        }}
      />

      {questionnaireName === "r-scale" && (
        <div className="flex flex-col gap-2">
          <p className="title-text text-center whitespace-pre-line">
            {t("r-scale.title")}
          </p>
          {currentStep === 0 && (
            <p className=" text-start whitespace-pre-line">
              {t("r-scale.intro")}
            </p>
          )}
        </div>
      )}

      {questionnaireName === "sgrq" && (
        <div className="flex flex-col gap-2">
          <p className="title-text text-center whitespace-pre-line">
            {t("sgrq.title")}
          </p>{" "}
          {currentStep === 0 && (
            <p className=" text-start whitespace-pre-line">{t("sgrq.intro")}</p>
          )}
          {currentStep === 0 && (
            <p className="muted-text text-start whitespace-pre-line">
              {t("sgrq.disclaimer")}
            </p>
          )}
        </div>
      )}

      {renderStep(
        steps,
        currentStep,
        formData,
        handleChange,
        goNext,
        filteredQuestions,
        translationForm,
      )}

      {/* <div className="flex justify-between">
        {currentStep > 0 ? (
          <button onClick={goPrev} className="outlined-button">
            Previous
          </button>
        ) : (
          <div />
        )}
        <button onClick={goNext} className="primary-button">
          {currentStep === totalSteps - 1 ? "Confirm" : "Next"}
        </button> */}

      <FormNavButtons
        hasPrev={currentStep > 0}
        onPrev={goPrev}
        onNext={goNext}
        isLast={currentStep === totalSteps - 1}
      />
    </div>
  );
}

export default DynamicQuestionnaireForm;
